import { useDraggable } from '@dnd-kit/react';
import type React from 'react';
import { useMemo } from 'react';
import { BoardTile } from './board-tile';
import type { Direction, TileData } from './board-types';
import type { TileDragData } from './tile-drag-overlay';

interface DraggableBoardTileProps {
  tile: TileData;
  cellSize: number;
  gap: number;
  padding: number;
  isDragging: boolean;
  /**
   * The direction this tile has been turned to mid-drag, while it differs from the tile's own. It
   * goes into the draggable's `data`, which `TileDragOverlay` reads — that is how a tile turned in
   * the air turns in the air, rather than only in the cells the board highlights beneath it.
   */
  dragDir?: Direction;
  /** The board's own droppable id — what the overlay compares a drop target against. */
  region: string;
  bare?: boolean;
  onKeyDown: (e: React.KeyboardEvent, tile: TileData) => void;
  onClick?: (e: React.MouseEvent, tile: TileData) => void;
}

/**
 * A board tile wired to dnd-kit's pointer drag.
 *
 * The node deliberately carries neither the drag translate nor `opacity: 0` while airborne: it stays
 * put at the origin and presents as the origin ghost, while `TileDragOverlay` renders the tile
 * following the pointer. Supplying an overlay is also what stops dnd-kit swapping in a placeholder for
 * this node, so it stays mounted and unmoved — which it must, because dnd-kit measures it to compute
 * the drop animation's landing rect.
 *
 * `isDropping` covers the window after that: the drag is over, so Board has already re-rendered this
 * node at its new cell, but the overlay is still in flight towards it. Hiding the node for those 150ms
 * keeps exactly one tile visible.
 */
export const DraggableBoardTile: React.FC<DraggableBoardTileProps> = ({
  tile,
  cellSize,
  gap,
  padding,
  isDragging,
  dragDir,
  region,
  bare = false,
  onKeyDown,
  onClick,
}) => {
  // A new object whenever the drag direction changes, which is what `useDraggable` watches to push
  // the change onto the draggable's reactive `data`.
  const airborne = useMemo<TileData>(
    () => (dragDir && dragDir !== tile.dir ? { ...tile, dir: dragDir } : tile),
    [tile, dragDir],
  );

  const { ref, isDropping } = useDraggable<TileDragData>({
    // The tile the overlay flies with is this canonical one — the ghosting below is presentation for
    // the origin node alone, and would otherwise render the tile in the air as an empty outline.
    id: tile.id,
    data: { tile: airborne, cellSize, gap, region },
  });

  return (
    <BoardTile
      ref={ref}
      tile={isDragging ? { ...tile, ghost: true } : tile}
      cellSize={cellSize}
      gap={gap}
      padding={padding}
      draggable
      isDragging={isDragging}
      bare={bare}
      style={isDropping ? { opacity: 0 } : undefined}
      onKeyDown={onKeyDown}
      onClick={onClick}
    />
  );
};
